/**
 * Карточка процесса по макету «Карточка процесса» (16.10.2026) — общие блоки дровера и страницы
 * /processes/[id]: итог под шапкой (всегда виден), вкладки «Маршрут · Детали · История» и их
 * содержимое. Макет задаёт состав, оформление — портала. Ничего не теряется: что было в длинной
 * ленте, теперь свёрнуто или лежит во вкладке.
 *
 * Вложения и начисление — во «Деталях», а не под маршрутом: это содержимое документа (резюме,
 * сканы приказов и договора, статус выплаты), и согласующий смотрит их вместе с реквизитами.
 * «Маршрут» остаётся коротким — ради него вкладку и открывают по умолчанию.
 */
import type { ReactNode } from 'react'
import { Stack, Text, XStack, YStack } from 'tamagui'

import { useApp } from '../../../i18n'
import { timeAgo } from '../../../lib/format'
import { tone } from '../../../theme/tones'
import { Avatar, Icon, Seg } from '../../../ui'
import type { ContractPayload, RequestCard } from '../../../lib/types'
import { PROCESS_STATUS, type RouteStep } from '../registry'
import { GROUP_LABEL, IT_WORKS_STEPS, STAGE_ACT, currentStep, itWorksStep, stepOf, type ProcVM } from '../processVM'
import { cancelInfo, isTerminal, noExecutorStep, overdueAt } from '../processCard'
import { isLocalTerminationDraft } from '../termination-types'
import { AccrualStatus, EDO_STATUS } from './EdoStatusBadge'
import { AgencyAnswerPanel } from './AgencyAnswerPanel'
import { AttachmentsBlock } from './AttachmentsBlock'
import { ItWorksModulesTable } from '../itWorks/ItWorksModulesTable'
import { AuditTimeline, isSystemEvent } from './AuditTimeline'
import { CardDetails, CounterpartyRisk, DetailRow } from './CardDetails'
import { NextStep, RouteTimeline } from './RouteTimeline'

export type CardTab = 'route' | 'details' | 'history'

/** Uppercase muted section label (как заголовки секций дровера). */
export function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <Text fontSize={11} fontWeight="700" color="$muted" textTransform="uppercase" letterSpacing={0.4}>
      {children}
    </Text>
  )
}

/** Плашка итога: иконка в кружке, заголовок, справа время, ниже одна-две строки пояснения. */
function Outcome({ tn, icon, title, at, sub, children }: { tn: string; icon: string; title: string; at?: string; sub?: string; children?: ReactNode }) {
  const c = tone(tn)
  const neutral = tn === 'default'
  return (
    <XStack gap={10} padding={12} borderRadius={7} borderWidth={1} backgroundColor={neutral ? '$bgLayout' : c.soft} borderColor={neutral ? '$borderSecondary' : c.soft}>
      <Stack width={28} height={28} flexShrink={0} borderRadius={999} backgroundColor={neutral ? '$borderSecondary' : c.solid} alignItems="center" justifyContent="center">
        <Icon name={icon} size={14} color={neutral ? '$muted' : '#fff'} />
      </Stack>
      <YStack flex={1} minWidth={0} gap={3} justifyContent="center" minHeight={28}>
        <XStack alignItems="center" justifyContent="space-between" gap={8}>
          <Text fontSize={13} fontWeight="700" color={neutral ? '$color' : c.text} flexShrink={1}>
            {title}
          </Text>
          {at ? (
            <Text fontSize={11} color="$muted" flexShrink={0}>
              {at}
            </Text>
          ) : null}
        </XStack>
        {sub ? (
          <Text fontSize={12} color="$color" lineHeight={17}>
            {sub}
          </Text>
        ) : null}
        {children}
      </YStack>
    </XStack>
  )
}

/**
 * Итог под шапкой: отзыв (кто, когда, причина), отказ (кем, комментарий), моё решение (шаг X из Y,
 * «срок истёк»), на ком сейчас — иначе. Красной строкой — шаг, который некому взять.
 */
export function CardSummary({ p, detail, route }: { p: ProcVM; detail?: RequestCard; route: RouteStep[] }) {
  const { t, l } = useApp()
  const cur = currentStep({ route })
  const status = detail?.status
  const warn = detail ? noExecutorStep(route) : undefined

  let body: ReactNode
  if (!detail) {
    // карточка ещё грузится: по строке списка, нейтрально — НЕ зелёное «завершён» у идущей заявки
    body =
      p.status === 'done' ? (
        <Outcome tn="green" icon="check-circle" title={l('proc.doneAll')} />
      ) : (
        <Outcome
          tn="blue"
          icon="clock"
          title={cur ? t(cur.act) : p.status === 'rejected' ? t({ ru: 'Ошибка проводки', en: 'Posting failed' }) : t({ ru: 'Согласование', en: 'Approval' })}
        />
      )
  } else if (isLocalTerminationDraft(detail)) {
    body = (
      <Outcome
        tn="default"
        icon="pencil"
        title={t({ ru: 'Черновик увольнения', en: 'Termination draft' })}
        sub={t({ ru: 'Черновик сохранён в портале. Откройте его, чтобы отправить на согласование.', en: 'The draft is saved in the portal. Open it to submit for approval.' })}
      />
    )
  } else if (status === 'CANCELLED') {
    const c = cancelInfo(detail)
    const title = c?.byAuthor === true ? l('card.cancelledByAuthor') : c?.byAuthor === false ? l('card.cancelledByHr') : t({ ru: 'Заявка отменена', en: 'Request cancelled' })
    const sub = c?.reason ? `«${c.reason}»${c.actor ? ` — ${c.actor}` : ''}` : c?.actor
    body = <Outcome tn="default" icon="x" title={title} at={c?.at ? t(timeAgo(c.at)) : undefined} sub={sub} />
  } else if (status === 'REJECTED') {
    const step = cur?.state === 'rejected' ? cur : undefined
    const sub = [step?.person?.name ? `${l('proc.rejectedBy')}: ${step.person.name}` : '', step?.comment ? `«${step.comment}»` : ''].filter(Boolean).join(' · ')
    body = <Outcome tn="red" icon="x" title={t(PROCESS_STATUS.rejected.label)} at={step?.when ? t(step.when) : undefined} sub={sub || undefined} />
  } else if (status === 'POST_FAILED') {
    body = <Outcome tn="red" icon="triangle-alert" title={t({ ru: 'Ошибка проводки — ожидает передачи в 1С', en: 'Posting failed — awaiting 1C' })} />
  } else if (isTerminal(status)) {
    // точная формулировка терминала из общей карты статусов (CLOSED/REGISTERED ≠ «Одобрено»)
    body = <Outcome tn="green" icon="check-circle" title={t((status && EDO_STATUS[status]?.label) ?? { ru: 'Одобрено', en: 'Approved' })} />
  } else if (detail.type === 'IT_WORKS' && status === 'DRAFT') {
    // Черновик IT-работ ещё не подан: шага и исполнителя нет — не «Согласование».
    body = (
      <Outcome
        tn="default"
        icon="pencil"
        title={t({ ru: 'Черновик', en: 'Draft', az: 'Qaralama' })}
        sub={t({ ru: 'Заявка ещё не отправлена на согласование.', en: 'The request has not been submitted for approval yet.', az: 'Müraciət hələ razılaşdırmaya göndərilməyib.' })}
      />
    )
  } else if (p.canDecide && p.taskId) {
    const at = route.findIndex((r) => r.state === 'current')
    const overdue = overdueAt(detail)
    const act = cur ? t(cur.act) : p.stage ? t(STAGE_ACT[p.stage]) : ''
    // IT_WORKS — по этапам плана, тем же счётом, что строка списка (itWorksStep); прочие — по маршруту
    const [n, m] = detail.type === 'IT_WORKS' ? [itWorksStep(detail.tasks), IT_WORKS_STEPS] : [at >= 0 ? at + 1 : stepOf(route), route.length]
    const sub = [`${l('doc.step')} ${n} ${l('doc.of')} ${m}`, act, overdue ? `${l('card.overdue')} ${t(timeAgo(overdue))}` : '']
      .filter(Boolean)
      .join(' · ')
    body = <Outcome tn="gold" icon="clock" title={l('card.yourDecision')} sub={sub} />
  } else {
    const openTask = detail.tasks?.find((tk) => String(tk.status) === 'OPEN')
    const act = openTask ? STAGE_ACT[openTask.stage] : undefined
    // Кто сейчас ответственный: персональный адресат → иначе название группы.
    const who: string | undefined =
      openTask?.assignee?.fullName ??
      (openTask?.candidateGroup ? t(GROUP_LABEL[openTask.candidateGroup] ?? { ru: openTask.candidateGroup, en: openTask.candidateGroup }) : undefined)
    body = (
      <Outcome tn="blue" icon="clock" title={act ? t(act) : t({ ru: 'Согласование', en: 'Approval' })}>
        {who ? (
          <XStack alignItems="center" gap={7}>
            {openTask?.assigneeId ? <Avatar name={who} seed={openTask.assigneeId} size="xs" /> : <Icon name="users" size={13} color="$tertiary" />}
            <Text fontSize={12} color="$muted" numberOfLines={1}>
              {l('proc.waiting')}: {who}
            </Text>
          </XStack>
        ) : null}
      </Outcome>
    )
  }

  return (
    <YStack gap={8}>
      {body}
      {warn ? (
        <XStack gap={8} paddingHorizontal={12} paddingVertical={9} borderRadius={7} backgroundColor={tone('red').soft} role="alert">
          <Icon name="triangle-alert" size={15} color={tone('red').text} />
          <Text flex={1} fontSize={12} lineHeight={17} color={tone('red').text}>
            {l('card.noExecutor').replace('{step}', t(warn.act))}
          </Text>
        </XStack>
      ) : null}
    </YStack>
  )
}

/** «Маршрут · Детали · История»; у «Истории» — число значимых (несистемных) событий. */
export function CardTabs({ value, onChange, detail }: { value: CardTab; onChange: (t: CardTab) => void; detail?: RequestCard }) {
  const { l } = useApp()
  const significant = (detail?.auditEvents ?? []).filter((e) => !isSystemEvent(e)).length
  const tabs: { key: CardTab; label: string; badge?: number }[] = [
    { key: 'route', label: l('card.tab.route') },
    { key: 'details', label: l('card.tab.details') },
    { key: 'history', label: l('card.tab.history'), badge: detail ? significant : undefined },
  ]
  return (
    <XStack role="tablist" aria-label={l('card.tabs')} gap={4} backgroundColor="$bgLayout" padding={4} borderRadius={7}>
      {tabs.map((tb) => (
        <Seg
          key={tb.key}
          active={value === tb.key}
          label={tb.label}
          badge={tb.badge}
          badgeTone="muted"
          onPress={() => onChange(tb.key)}
          flex={1}
          minHeight={44}
          justifyContent="center"
        />
      ))}
    </XStack>
  )
}

/** Содержимое выбранной вкладки. */
export function CardTabContent({ tab, p, detail, route }: { tab: CardTab; p: ProcVM; detail?: RequestCard; route: RouteStep[] }) {
  const { t } = useApp()
  const draft = !!detail && isLocalTerminationDraft(detail)

  if (tab === 'history') {
    return detail ? <AuditTimeline events={detail.auditEvents ?? []} /> : null
  }

  if (tab === 'details') {
    if (!detail) return null
    return (
      <YStack gap={14}>
        <CardDetails card={detail} />
        {/* Только содержимое документа. «Создан» — в шапке рядом с номером, «Обновлён» — в «Истории»:
            здесь их принимали за даты отпуска (30.10.2026). */}
        {detail.isSigned && detail.signedBy?.fullName ? <DetailRow k={t({ ru: 'Подписал', en: 'Signed by' })} v={detail.signedBy.fullName} /> : null}
        {/* Риск-статус контрагента (ТЗ-2026-015 §7.2): в заявке лежит только ВÖİN, а смотреть
            на риск обязан согласующий юрист — не гонять же его в госпортал руками. */}
        {detail.type === 'CONTRACT' && <CounterpartyRisk contract={detail.payload as unknown as ContractPayload} />}
        {/* IT-работы: строки модулей с решениями — до вложений, отчёты строк лежат там же. У черновика
            решений нет: сервер отдаёт все строки «Ожидает решения» — до подачи таблицу не показываем. */}
        {detail.type === 'IT_WORKS' && detail.status !== 'DRAFT' && detail.itWorks?.lines.length ? (
          <YStack gap={10}>
            <SectionLabel>{t({ ru: 'Модули', en: 'Modules', az: 'Modullar' })}</SectionLabel>
            <ItWorksModulesTable card={detail} />
          </YStack>
        ) : null}
        {/* Уточнение турагентства: панель ответа куратору — бэк отдаёт флаг только ему и только
            пока вопрос открыт. Сам тред лежит в «Истории» обычными событиями заявки. */}
        <AgencyAnswerPanel card={detail} />
        {/* вложения (резюме кандидата, скан приказа, подписанный договор) — защищённое скачивание */}
        {detail.attachments?.length ? (
          <YStack gap={10}>
            <SectionLabel>{t({ ru: 'Вложения', en: 'Attachments' })}</SectionLabel>
            <AttachmentsBlock attachments={detail.attachments} requestId={detail.id} canEditOrders={detail.canEditOrderScans} />
          </YStack>
        ) : null}
        {/* начисление отпускных (ТЗ-2026-004 шаг 11): статус выплаты */}
        <AccrualStatus detail={detail} />
      </YStack>
    )
  }

  return (
    <YStack>
      {draft || p.approvalRouteUnavailable ? (
        <Text fontSize={13} color="$muted">
          {t({ ru: 'Маршрут появится после отправки на согласование.', en: 'The route appears after submission.' })}
        </Text>
      ) : (
        <>
          <NextStep route={route} />
          <RouteTimeline route={route} collapse={isTerminal(detail?.status) ? 'terminal' : 'active'} />
        </>
      )}
      {/* «Информируемые» (ТЗ-2026-015 §5.1) — участники без задачи: в маршруте их нет,
          но кто ещё читает документ, видеть надо. */}
      {!!detail?.observers?.length && (
        <YStack gap={6} marginTop={14}>
          <SectionLabel>{t({ ru: 'Информируемые', en: 'Informed' })}</SectionLabel>
          {detail.observers.map((o) => (
            <XStack key={o.id} alignItems="center" gap={8}>
              <Icon name="eye" size={13} color="$muted" />
              <Text fontSize={13} color="$color">
                {o.fullName}
              </Text>
              {!!o.jobTitle && (
                <Text fontSize={11} color="$muted">
                  · {o.jobTitle}
                </Text>
              )}
            </XStack>
          ))}
          <Text fontSize={11} color="$muted">
            {t({ ru: 'Получили уведомление и доступ к документу; согласование не блокируют.', en: 'Notified and granted access; they do not block the approval.' })}
          </Text>
        </YStack>
      )}
    </YStack>
  )
}
